"use client";

import { ReactNode, useId, useState } from "react";
import css from "./accordion.module.scss";
import Image from "../image/image";

export enum AccordionStyle {
  DEFAULT = "default",
  MINIMAL = "minimal",
  PLAIN = "plain",
}

interface IAccordionProps {
  title: ReactNode;
  children: ReactNode;
  accordionStyle?: AccordionStyle;
  className?: string;
  titleClassName?: string;
  panelClassName?: string;
  expanded?: boolean;
  showToggle?: boolean;
}

const accordionStyles: Record<
  AccordionStyle,
  {
    root: string;
    open: string;
    title: string;
    panel: string;
  }
> = {
  [AccordionStyle.DEFAULT]: {
    root: css.accordion,
    open: css.open,
    title: `${css.accordionTitle} !pl-4`,
    panel: `${css.accordionDescription} !pl-4`,
  },

  [AccordionStyle.MINIMAL]: {
    root: "accordion",
    open: "",
    title: "accordionTitle",
    panel: "accordionContent",
  },

  [AccordionStyle.PLAIN]: {
    root: "",
    open: "",
    title: "",
    panel: "",
  },
};

const joinClasses = (...classes: (string | false | undefined)[]) =>
  classes.filter(Boolean).join(" ");

export default function Accordion({
  title,
  children,
  accordionStyle = AccordionStyle.DEFAULT,
  className,
  titleClassName,
  panelClassName,
  expanded = false,
  showToggle = false,
}: IAccordionProps) {
  const [open, setOpen] = useState(expanded);
  const panelId = useId();

  const styles = accordionStyles[accordionStyle];

  return (
    <div className={joinClasses(styles.root, open && styles.open, className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((previous) => !previous)}
        className={joinClasses(styles.title, titleClassName)}
      >
        {title}

        {showToggle && (
          <span className="ml-auto flex shrink-0 items-center gap-2 text-sm font-semibold text-amc-blue-primary">
            <span className="hidden md:inline">{open ? "Hide" : "Show"}</span>

            <Image
              src="/down-arrow.svg"
              alt=""
              width={16}
              height={16}
              className={`transition-transform duration-150 ease-out ${
                open ? "rotate-180" : ""
              }`}
            />
          </span>
        )}
      </button>

      <div
        id={panelId}
        hidden={!open}
        className={joinClasses(styles.panel, panelClassName)}
      >
        {children}
      </div>
    </div>
  );
}
